import type { ChannelInfo, GuestInvite } from "@rhema/client";
import {
  Button,
  CheckIcon,
  ChevronLeftIcon,
  CopyIcon,
  cn,
  EmptyState,
  focusRing,
  LinkIcon,
  LockIcon,
  PageHeader,
  Switch,
  UiLink,
  UserPlusIcon,
} from "@rhema/ui";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useFailure } from "../billing/UpgradeLink";
import { useFeedback } from "../menus/Feedback";
import { ShareTargets } from "../people/ShareTargets";
import { useApi, useClient, useClientState, useSession } from "../session/session";
import { isAdmin, isForeign, shareLink, spaceHref, spaceInviteLink, spaceOf } from "./data";
import { GuestInviteSheet } from "./SpaceSheets";
import { SpaceApps, SpaceWorkflows } from "./Workflows";

/** A link to copy, with the ways to send it. */
function LinkBox({ link, text, subject }: { link: string; text: string; subject: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 rounded-field border border-line bg-linen px-3 py-2.5">
        <LinkIcon size={15} className="flex-none text-ink-secondary" />
        <span className="min-w-0 flex-1 truncate text-[13.5px] select-text">{link}</span>
        <Button
          size="sm"
          variant="ghost"
          icon={copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
          onClick={() =>
            void navigator.clipboard
              ?.writeText(link)
              .then(() => setCopied(true))
              .catch(() => undefined)
          }
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <ShareTargets
        text={text}
        url={link}
        subject={subject}
        qrCode={false}
        onResult={(r) => r.note?.startsWith("Copied") && setCopied(true)}
      />
    </div>
  );
}

/**
 * A space's admin page. Admins turn Queravel, Malakh and Penielly on or off for the space's
 * channels and manage its workflows. They see every channel, private ones included: they make
 * channels private or public, set which ones everyone joins and who posts, share channels with
 * other spaces, and delete channels. Guest links are listed here too, to turn off.
 */
export function SpaceChannelsAdmin({ spaceId, variant }: { spaceId: string; variant: "phone" | "desktop" }) {
  const api = useApi();
  const client = useClient();
  const feedback = useFeedback();
  const state = useClientState();
  const { platform } = useSession();
  const [params, setParams] = useSearchParams();
  const space = spaceOf(state, spaceId);
  const admin = isAdmin(space);
  const [channels, setChannels] = useState<ChannelInfo[] | null>(null);
  const [guestLinks, setGuestLinks] = useState<GuestInvite[] | null>(null);
  const [inviting, setInviting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const focused = useRef<HTMLLIElement | null>(null);
  const { error, fail, clear } = useFailure();
  const origin = platform.serverOrigin() || window.location.origin;

  const load = () =>
    Promise.all([
      client.allChannels(spaceId).then(setChannels),
      // Guest links are a separate list, so the channels still show if it fails to load.
      api
        .guestInvites(spaceId)
        .then(setGuestLinks)
        .catch(() => setGuestLinks([])),
    ]).catch((err) => fail(err));
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloads when the space changes
  useEffect(() => {
    if (admin) void load();
  }, [admin, client, spaceId]);

  // "Share with another space" from a channel's menu opens here with its share link ready.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once the channels have loaded
  useEffect(() => {
    const id = params.get("share");
    if (!id || !channels) return;
    const p = new URLSearchParams(params);
    p.delete("share");
    setParams(p, { replace: true });
    setFocus(id);
    const c = channels.find((x) => x.id === id);
    if (c && !c.share_code && !isForeign(c, spaceId)) void act(c.id, () => api.shareChannel(spaceId, c.id));
  }, [channels, params]);

  useEffect(() => {
    if (focus) focused.current?.scrollIntoView?.({ block: "center" });
  }, [focus]);

  if (!space) return <EmptyState title="Space not found" className="flex-1" />;
  if (!admin)
    return (
      <EmptyState title="Only admins manage the space" className="flex-1">
        Ask an admin of {space.name} to change its channels, apps or workflows.
      </EmptyState>
    );

  const act = async (id: string, job: () => Promise<unknown>) => {
    setBusy(id);
    clear();
    try {
      await job();
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const confirmThen = (
    title: string,
    detail: string,
    action: string,
    id: string,
    job: () => Promise<unknown>,
  ) =>
    void feedback.confirm({ title, detail, action }).then((yes) => {
      if (yes) void act(id, job);
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className={variant === "desktop" ? "mx-auto w-full max-w-[680px] pb-10" : "pb-24"}>
        <div className="px-4 pt-4">
          <UiLink
            href={spaceHref(space.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded px-1 text-sm font-semibold text-blue no-underline",
              focusRing,
            )}
          >
            <ChevronLeftIcon size={16} strokeWidth={2.2} />
            {space.name}
          </UiLink>
        </div>
        <PageHeader title="Manage space" size={variant === "desktop" ? "md" : "lg"} className="pt-2" />
        <div className="flex flex-col gap-7 px-4">
          <SpaceApps space={space} />
          <SpaceWorkflows space={space} />
        </div>
        <div className="mt-7 flex flex-col gap-3 px-4">
          <h2 className="m-0 text-[17px] font-bold">Channels</h2>
          <p className="m-0 text-sm leading-normal text-ink-secondary">
            Admins see every channel here, including private ones they are not in. Messages in a private
            channel stay readable only by its members.
          </p>
          {error && (
            <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
              {error}
            </p>
          )}
          {!channels && !error && <p className="m-0 text-sm text-ink-meta">Loading the channels.</p>}
          <ul aria-label="Channels" className="m-0 flex list-none flex-col gap-2.5 p-0">
            {channels?.map((c) => {
              const foreign = isForeign(c, space.id);
              const shared = c.shared_with ?? [];
              return (
                <li
                  key={c.id}
                  ref={focus === c.id ? focused : undefined}
                  className={cn(
                    "rounded-card border bg-surface px-4 py-3.5",
                    focus === c.id ? "border-blue" : "border-line",
                  )}
                >
                  <div className="flex items-center gap-2">
                    {c.private && <LockIcon size={15} className="text-ink-secondary" label="Private" />}
                    <span className="min-w-0 flex-1 truncate text-[15px] font-bold">#{c.name}</span>
                    <span className="flex-none text-[13px] text-ink-secondary">
                      {c.member_count} {c.member_count === 1 ? "member" : "members"}
                    </span>
                  </div>
                  {c.topic && <p className="m-0 mt-1 text-[13.5px] text-ink-secondary">{c.topic}</p>}
                  {foreign ? (
                    <div className="mt-3 flex flex-col gap-2.5">
                      <p className="m-0 flex items-center gap-1.5 text-[13.5px] text-ink-secondary">
                        <LinkIcon size={14} />
                        Shared from {c.space_name || "another space"}, which manages it.
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="self-start text-scarlet"
                        disabled={busy === c.id}
                        onClick={() =>
                          confirmThen(
                            `Remove #${c.name} from ${space.name}?`,
                            `People in ${space.name} leave it unless they are also in ${c.space_name || "the space that owns it"}. Only that space can share it again.`,
                            "Remove",
                            c.id,
                            () => client.stopSharing(space.id, c.id, space.id),
                          )
                        }
                      >
                        Remove from {space.name}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="mt-3 flex flex-col gap-2.5">
                        <div className="flex items-center justify-between gap-3 text-[14.5px]">
                          <span className="flex flex-col">
                            <span className="font-semibold">Private</span>
                            <span className="text-[12.5px] text-ink-secondary">
                              Only people added to it can find it and read it.
                            </span>
                          </span>
                          <Switch
                            label={`#${c.name} is private`}
                            checked={c.private}
                            disabled={busy === c.id || c.auto_join}
                            onChange={(on) =>
                              void act(c.id, () => client.updateChannel(space.id, c.id, { private: on }))
                            }
                          />
                        </div>
                        <div className="flex items-center justify-between gap-3 text-[14.5px]">
                          <span className="flex flex-col">
                            <span className="font-semibold">Everyone joins</span>
                            <span className="text-[12.5px] text-ink-secondary">
                              Everyone in the space is in it, and new members join it. Guests are not.
                            </span>
                          </span>
                          <Switch
                            label={`Everyone joins #${c.name}`}
                            checked={c.auto_join}
                            disabled={busy === c.id || c.private}
                            onChange={(on) =>
                              void act(c.id, () => client.updateChannel(space.id, c.id, { auto_join: on }))
                            }
                          />
                        </div>
                        {c.joined && (
                          <div className="flex items-center justify-between gap-3 text-[14.5px]">
                            <span className="flex flex-col">
                              <span className="font-semibold">Only admins post</span>
                              <span className="text-[12.5px] text-ink-secondary">
                                Members read, react and reply in threads.
                              </span>
                            </span>
                            <Switch
                              label={`Only admins post in #${c.name}`}
                              checked={c.announcement}
                              disabled={busy === c.id}
                              onChange={(on) =>
                                void act(c.id, () => api.updateChat(c.id, { only_admins_post: on }))
                              }
                            />
                          </div>
                        )}
                      </div>

                      <section
                        aria-label={`Sharing #${c.name}`}
                        className="mt-3 flex flex-col gap-2 border-t border-line pt-3"
                      >
                        <span className="text-[14.5px] font-semibold">Shared with other spaces</span>
                        {shared.length === 0 && !c.share_code && (
                          <span className="text-[12.5px] text-ink-secondary">
                            An admin of another space opens a share link to add this channel there. People in
                            both spaces can then talk in it.
                          </span>
                        )}
                        {shared.map((s) => (
                          <div key={s.id} className="flex items-center gap-2 text-[14px]">
                            <LinkIcon size={14} className="text-ink-secondary" />
                            <span className="min-w-0 flex-1 truncate">{s.name}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-scarlet"
                              disabled={busy === c.id}
                              onClick={() =>
                                confirmThen(
                                  `Stop sharing #${c.name} with ${s.name}?`,
                                  `People in ${s.name} leave the channel unless they are also in ${space.name}. Sharing it again needs a new link.`,
                                  "Stop sharing",
                                  c.id,
                                  () => client.stopSharing(space.id, c.id, s.id),
                                )
                              }
                            >
                              Stop sharing
                            </Button>
                          </div>
                        ))}
                        {c.share_code ? (
                          <>
                            <span className="text-[12.5px] text-ink-secondary">
                              Send this link to an admin of the other space. It works once.
                            </span>
                            <LinkBox
                              link={shareLink(c.share_code, origin)}
                              text={`Add #${c.name} from ${space.name} to your space on Rhema: ${shareLink(c.share_code, origin)}`}
                              subject={`Share #${c.name} on Rhema`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="self-start text-scarlet"
                              disabled={busy === c.id}
                              onClick={() => void act(c.id, () => api.revokeShare(space.id, c.id))}
                            >
                              Turn off the share link
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="self-start"
                            icon={<LinkIcon size={15} />}
                            disabled={busy === c.id}
                            onClick={() => void act(c.id, () => api.shareChannel(space.id, c.id))}
                          >
                            Share with another space
                          </Button>
                        )}
                      </section>

                      <div className="mt-3 border-t border-line pt-3">
                        {deleting === c.id ? (
                          <div className="flex flex-col gap-2">
                            <p className="m-0 text-sm leading-normal">
                              Delete #{c.name} with every message and file in it, for everyone
                              {shared.length ? " in every space it is shared with" : ""}? This cannot be
                              undone.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="danger-fill"
                                disabled={busy === c.id}
                                onClick={() =>
                                  void act(c.id, async () => {
                                    await client.deleteChannel(space.id, c.id);
                                    setDeleting(null);
                                  })
                                }
                              >
                                Delete the channel
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setDeleting(null)}>
                                Keep it
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-scarlet"
                            disabled={channels.filter((x) => !isForeign(x, space.id)).length <= 1}
                            onClick={() => setDeleting(c.id)}
                          >
                            Delete #{c.name}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          <section aria-label="Guest links" className="mt-4 flex flex-col gap-2.5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="m-0 text-[17px] font-bold">Guests</h2>
              <Button size="sm" icon={<UserPlusIcon size={16} />} onClick={() => setInviting(true)}>
                Invite a guest
              </Button>
            </div>
            <p className="m-0 text-sm leading-normal text-ink-secondary">
              Guests are only in the channels they are invited to. Each guest link names its channels. Turn a
              link off to stop new guests joining with it.
            </p>
            {guestLinks?.length === 0 && (
              <p className="m-0 text-sm text-ink-meta">No guest links are open.</p>
            )}
            <ul aria-label="Open guest links" className="m-0 flex list-none flex-col gap-2.5 p-0">
              {guestLinks?.map((g) => (
                <li
                  key={g.code}
                  className="flex flex-col gap-2 rounded-card border border-line bg-surface px-4 py-3"
                >
                  <span className="text-[14.5px] font-semibold">
                    {g.channels.map((c) => `#${c.name}`).join(", ") || "No channels left"}
                  </span>
                  <LinkBox
                    link={spaceInviteLink(g.code, origin)}
                    text={`Join ${space.name} on Rhema as a guest: ${spaceInviteLink(g.code, origin)}`}
                    subject={`Join ${space.name} on Rhema`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    className="self-start text-scarlet"
                    disabled={busy === g.code}
                    onClick={() => void act(g.code, () => api.revokeGuestInvite(space.id, g.code))}
                  >
                    Turn off the link
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
      <GuestInviteSheet
        space={space}
        open={inviting}
        onClose={() => {
          setInviting(false);
          void load();
        }}
      />
    </div>
  );
}
